import { Checkbox } from '@domgen/base-design-system-2.0/atoms/Checkbox';
import { PhoneInput } from '@domgen/base-design-system-2.0/atoms/PhoneInput/PhoneInput';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { CircleCheck } from '../../../assets/icons/CircleCheck';
import { AddressSearchResult } from '@domgen/shared/models/services/api/getAddresses';
import { ChangeEvent } from 'react';
import { FormChangeEvent } from '../../../hooks/useFormValidation';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { AddressForm } from './AddressForm';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { ApplianceFormData } from '../../../models/form/appliance';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';
import { useStoreContext } from '../../../store/selectors/useStoreContext';

interface PersonalDetailsFormProps {
  formValues: Partial<ApplianceFormData[StepKey.PERSONAL_DETAILS]>;
  errors: Record<string, string>;
  handleChange: (e: FormChangeEvent) => string | void;
  onBlur: (e: FormChangeEvent) => string | void;
  onAddressSelect: (address: AddressSearchResult, addressType?: 'mailing' | 'merchandise') => void;
  setMerchandiseLocatedAtSameAddress: (value: boolean) => void;
  merchandiseLocatedAtSameAddress: boolean;
  isMailingAddressPoBox: boolean;
  applianceCategory: string;
  sessionId: string;
  systemCode: string;
}

export function PersonalDetailsForm({
  formValues,
  errors,
  handleChange,
  onBlur,
  onAddressSelect,
  setMerchandiseLocatedAtSameAddress,
  merchandiseLocatedAtSameAddress,
  isMailingAddressPoBox,
  applianceCategory,
  sessionId,
  systemCode,
}: PersonalDetailsFormProps) {
  const storeContext = useStoreContext();

  const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setMerchandiseLocatedAtSameAddress(e.target.checked);

    capturePostHogEvent(
      posthog,
      e.target.checked ? 'location-address-ticked' : 'location-address-unticked',
      storeContext,
    );

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Same as where my product is located',
      eventLabel: e.target.checked ? 'ticked' : 'unticked',
      type: 'STANDARD',
      pagename: 'olr-personal-details',
      applianceCategory,
      category: 'registration-pages',
      appSessionID: sessionId,
      regSystemCode: systemCode,
    });
  };

  const posthog = usePostHog();
  return (
    <>
      <div className="col-span-8 md:col-span-4">
        <Input
          label="First name"
          placeholder="Enter first name"
          name={FormKey.FIRST_NAME}
          type="text"
          value={formValues?.[FormKey.FIRST_NAME]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.FIRST_NAME]}
          rightIcon={
            formValues?.[FormKey.FIRST_NAME] && !errors?.[FormKey.FIRST_NAME] ? (
              <CircleCheck />
            ) : undefined
          }
          hasSuccess={formValues?.[FormKey.FIRST_NAME] !== '' && !errors?.[FormKey.FIRST_NAME]}
          id="firstName"
          autoComplete={AutoComplete.FIRST_NAME}
        />
      </div>
      <div className="col-span-8 md:col-span-4">
        <Input
          label="Last name"
          placeholder="Enter last name"
          name={FormKey.LAST_NAME}
          type="text"
          value={formValues?.[FormKey.LAST_NAME]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.LAST_NAME]}
          rightIcon={
            formValues?.[FormKey.LAST_NAME] && !errors?.[FormKey.LAST_NAME] ? (
              <CircleCheck />
            ) : undefined
          }
          hasSuccess={formValues?.[FormKey.LAST_NAME] !== '' && !errors?.[FormKey.LAST_NAME]}
          id="lastName"
          autoComplete={AutoComplete.LAST_NAME}
        />
      </div>
      <AddressForm
        formValues={formValues}
        errors={errors}
        onAddressSelect={onAddressSelect}
        handleFormChange={handleChange}
        onBlur={onBlur}
        addressType="mailing"
        isMailingAddressPoBox={isMailingAddressPoBox}
        applianceCategory={applianceCategory}
        sessionId={sessionId}
        systemCode={systemCode}
      />
      <div className="col-span-8">
        <Checkbox
          label="Same as where my product is located"
          checked={merchandiseLocatedAtSameAddress}
          disabled={isMailingAddressPoBox}
          onChange={handleCheckbox}
        />
      </div>
      <div className="col-span-8">
        <Input
          label="Email"
          placeholder="Enter email"
          name="email"
          type="email"
          value={formValues?.[FormKey.EMAIL]}
          onChange={handleChange}
          onBlur={onBlur}
          hasError={errors?.[FormKey.EMAIL]}
          rightIcon={
            formValues?.[FormKey.EMAIL] && !errors?.[FormKey.EMAIL] ? <CircleCheck /> : undefined
          }
          hasSuccess={formValues?.[FormKey.EMAIL] !== '' && !errors?.[FormKey.EMAIL]}
          id="email"
          autoComplete={AutoComplete.EMAIL}
        />
      </div>
      <div className="col-span-8">
        <div className="flex flex-col gap-space-200">
          <PhoneInput
            autoComplete={AutoComplete.PHONE_NUMBER}
            placeholder="Enter phone number"
            label="Phone number"
            name={FormKey.PHONE_NUMBER}
            value={formValues?.[FormKey.PHONE_NUMBER]}
            onChange={handleChange}
            onBlur={onBlur}
            hasError={errors?.[FormKey.PHONE_NUMBER]}
            rightIcon={
              formValues?.[FormKey.PHONE_NUMBER] && !errors?.[FormKey.PHONE_NUMBER] ? (
                <CircleCheck />
              ) : undefined
            }
            hasSuccess={
              formValues?.[FormKey.PHONE_NUMBER] !== '' && !errors?.[FormKey.PHONE_NUMBER]
            }
            id="phoneNumber"
          />
        </div>
      </div>
    </>
  );
}
